import { useEffect, useMemo, useState } from "react";
import {
  FaChartBar,
  FaCommentDots,
  FaFileAlt,
  FaMoneyBillWave,
  FaPhoneAlt,
} from "react-icons/fa";
import ActionPageLayout from "../layouts/ActionPageLayout";
import usePageTitle from "../hooks/usePageTitle";
import { fetchLiveDashboard } from "../services/liveDashboardApi";

const defaultApplicationCards = [
  {
    icon: FaFileAlt,
    iconClassName: "text-[#d9835d]",
    title: "Total Appl. worked Today",
    lines: [
      ["SUBMITTED", 4],
      ["DRAFT", 8],
      ["APPROVED", 3],
      ["REJECTED", 1],
    ],
  },
  {
    icon: FaMoneyBillWave,
    iconClassName: "text-[#d38a26]",
    title: "Total Disbursed Today",
    lines: [
      ["Count", 0],
      ["Amount(lac)", "0.00"],
    ],
  },
  {
    icon: FaMoneyBillWave,
    iconClassName: "text-[#d38a26]",
    title: "Total MTD Disbursal",
    subtitle: "(Till Yesterday)",
    lines: [
      ["Count", 297],
      ["Amount(lac)", "192.00"],
    ],
  },
  {
    icon: FaPhoneAlt,
    iconClassName: "text-[#e13c8a]",
    title: "Total Calls Received",
    subtitle: "Today",
    value: "0",
  },
  {
    icon: FaCommentDots,
    iconClassName: "text-[#b4a6ea]",
    title: "Total WhatsApp Received",
    subtitle: "Today",
    value: "0",
  },
];

const telecallingHeaders = [
  "Agent",
  "Leads",
  "Calls",
  "Connected",
  "Not Interested",
  "Not Eligible",
  "Processed",
  "Follow-up",
  "In Credit Check",
  "Credit Reject",
  "Appointments",
];

const telecallingFields = [
  "agent", "assigned", "called", "connected", "ni", "not_eligible",
  "already_processed", "followup", "in_credit_check", "num_of_reject", "appointment",
];

const AUTO_REFRESH_INTERVAL_MS = 5 * 60 * 1000;
const AUTO_REFRESH_INTERVAL_SECONDS = AUTO_REFRESH_INTERVAL_MS / 1000;

function parseSummaryLines(value) {
  if (value && typeof value === "object") return Object.entries(value);
  if (typeof value !== "string") return [["Count", value ?? 0]];
  const matches = [...value.matchAll(/([^:/]+):\s*([^/]+)(?:\s*\/\s*|$)/g)];
  return matches.length
    ? matches.map(([, label, item]) => [label.trim(), item.trim()])
    : [["Count", value]];
}

function SummaryCard({ card }) {
  const Icon = card.icon;

  return (
    <article className="flex min-h-[152px] flex-col items-center justify-start rounded-[0.5rem] border border-[#d1d5db] bg-white p-4 text-center shadow-[0_2px_8px_rgba(15,23,42,0.12)]">
      <div className="mb-1.5 flex items-center gap-2">
        <Icon
          className={`h-4 w-4 flex-none ${card.iconClassName}`}
          aria-hidden="true"
        />
        <h2 className="m-0 text-base font-semibold text-[#111827]">
          {card.title}
        </h2>
      </div>

      {card.subtitle ? (
        <p className="m-0 text-base leading-[1.25] text-[#111827]">
          {card.subtitle}
        </p>
      ) : null}

      {Array.isArray(card.lines) ? (
        <div className="mt-1.5 grid gap-1 text-base font-bold leading-[1.25] text-[#111827]">
          {card.lines.map(([label, value]) => (
            <p className="m-0" key={`${card.title}-${label}`}>
              {label} : {value}
            </p>
          ))}
        </div>
      ) : null}

      {card.value !== undefined ? (
        <div className="mt-4 text-[1.875rem] font-bold leading-none text-[#111827]">
          {card.value}
        </div>
      ) : null}
    </article>
  );
}

function SectionTitle({ icon: Icon, iconClassName = "text-[#e13c8a]", title }) {
  return (
    <h2 className="mb-3 flex items-center gap-3 text-[1.75rem] font-bold text-[#171717] max-[720px]:text-2xl">
      <Icon
        className={`h-8 w-8 flex-none ${iconClassName}`}
        aria-hidden="true"
      />
      {title}
    </h2>
  );
}

function DataTable({ headers, rows, totalRow, totalRowClassName = "" }) {
  return (
    <div className="overflow-x-auto border border-[#d9dfe8] bg-white shadow-[0_2px_8px_rgba(15,23,42,0.08)]">
      <table className="w-full min-w-[1500px] border-collapse text-left text-base">
        <thead>
          <tr>
            {headers.map((header) => (
              <th
                className="border-b border-r border-[#d9dfe8] bg-[#f8f9fa] px-3 py-3 font-bold text-black last:border-r-0"
                key={header}
                scope="col"
              >
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row[0]}>
              {row.map((cell, index) => (
                <td
                  className="border-r border-t border-[#d9dfe8] px-3 py-2.5 last:border-r-0"
                  key={`${row[0]}-${headers[index]}`}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
          <tr className={totalRowClassName}>
            {totalRow.map((cell, index) => (
              <td
                className="border-r border-t border-[#d9dfe8] px-3 py-2.5 font-bold last:border-r-0"
                key={`${totalRow[0]}-${headers[index]}`}
              >
                {cell}
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function LiveDashboardPage() {
  usePageTitle("Live Dashboard | Pargro");
  const [dashboard, setDashboard] = useState(null);
  const [refreshSeconds, setRefreshSeconds] = useState(AUTO_REFRESH_INTERVAL_SECONDS);

  useEffect(() => {
    let active = true;
    let nextRefreshAt = Date.now() + AUTO_REFRESH_INTERVAL_MS;

    const loadDashboard = () => {
      fetchLiveDashboard()
        .then((dashboardData) => {
          if (active) {
            setDashboard(dashboardData);
          }
        })
        .catch(() => {});
    };

    const updateCountdown = () => {
      const secondsRemaining = Math.max(
        0,
        Math.ceil((nextRefreshAt - Date.now()) / 1000),
      );
      if (active) setRefreshSeconds(secondsRemaining);
    };

    loadDashboard();
    const countdownId = window.setInterval(updateCountdown, 1000);
    const refreshId = window.setInterval(() => {
      nextRefreshAt = Date.now() + AUTO_REFRESH_INTERVAL_MS;
      setRefreshSeconds(AUTO_REFRESH_INTERVAL_SECONDS);
      loadDashboard();
    }, AUTO_REFRESH_INTERVAL_MS);

    return () => {
      active = false;
      window.clearInterval(countdownId);
      window.clearInterval(refreshId);
    };
  }, []);

  const applicationCards = useMemo(() => {
    if (!dashboard) {
      return defaultApplicationCards.map((card) => ({
        ...card,
        lines: card.lines?.map(([label]) => [label, 0]),
        value: card.value !== undefined ? 0 : undefined,
      }));
    }
    const summary = dashboard.summary || {};
    return [
      { ...defaultApplicationCards[0], lines: parseSummaryLines(summary.login) },
      { ...defaultApplicationCards[1], lines: parseSummaryLines(summary.total_disbursed) },
      { ...defaultApplicationCards[2], lines: parseSummaryLines(summary.total_cum_disbursal) },
      { ...defaultApplicationCards[3], value: summary.total_inbound ?? 0 },
      { ...defaultApplicationCards[4], value: summary.total_whatsapp ?? 0 },
    ];
  }, [dashboard]);

  const telecallingRows = (dashboard?.telecalling_summary?.rows || []).map((row) =>
    telecallingFields.map((field) => row?.[field] ?? (field === "agent" ? "" : 0)),
  );
  const telecallingTotal = ["Total", ...telecallingFields.slice(1).map(
    (field) => dashboard?.telecalling_summary?.totals?.[field] ?? 0,
  )];

  return (
    <ActionPageLayout
      title="Live Dashboard"
      headerStatus={`Auto-refresh in ${refreshSeconds} sec`}
      icon={
        <FaChartBar
          className="h-6 w-6 flex-none text-[#6d4b8a]"
          aria-hidden="true"
        />
      }
    >
      <section className="mt-6">
        <div className="grid grid-cols-5 gap-4 max-[1400px]:grid-cols-3 max-[980px]:grid-cols-2 max-[640px]:grid-cols-1">
          {applicationCards.map((card) => (
            <SummaryCard card={card} key={card.title} />
          ))}
        </div>

        <section className="mt-4">
          <SectionTitle icon={FaPhoneAlt} title="Today's Telecalling Summary" />
          <DataTable
            headers={telecallingHeaders}
            rows={telecallingRows}
            totalRow={telecallingTotal}
          />
        </section>

      </section>
    </ActionPageLayout>
  );
}

export default LiveDashboardPage;
